import {useEffect, useEffectEvent, useRef, useState} from 'react'
import type {ChatState} from '@microsoft/agent-host-protocol'
import type {
  DirectTurnTelemetryContext,
  EstablishedAhpSession,
  PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import {isReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {HOST_INDEPENDENT_CLIENT_TOOLS} from '@github-ui/copilot-shared-ui/utils/ahp-client-tools'

import type {Session} from '../types/session'
import {bindUserStartedTurn, markUserStartedTurn, unmarkUserStartedTurn} from '../utils/artifacts/user-started-turns'

export function persistedFirstTurnPrompt(
  eventContent: Session['event_content'] | undefined,
  model: Session['model'],
  persistedReasoningEffort: Session['reasoning_effort'],
): PreparedPrompt | undefined {
  if (!eventContent) return undefined

  const reasoningEffort = isReasoningEffort(persistedReasoningEffort) ? persistedReasoningEffort : undefined
  return {
    text: eventContent,
    ...(model
      ? {
          model: {
            id: model,
            ...(reasoningEffort ? {config: {reasoningEffort}} : {}),
          },
        }
      : {}),
  }
}

function isUnstartedChat(state: ChatState): boolean {
  return (
    state.turns.length === 0 &&
    !state.activeTurn &&
    !state.turnsNextCursor &&
    !state.steeringMessage &&
    !state.queuedMessages?.length
  )
}

export function useFirstTurnRecoverySelection(sessionId: string | undefined, recoverable: boolean): boolean {
  const [selection, setSelection] = useState({sessionId, recoverable})
  if (selection.sessionId === sessionId) return selection.recoverable
  setSelection({sessionId, recoverable})
  return recoverable
}

export function useAhpFirstTurnRecovery({
  session,
  prompt,
  sessionId,
  taskId,
  interactiveSandboxChat,
  directTurnTelemetryContext,
  enabled,
}: {
  session: EstablishedAhpSession | undefined
  prompt: PreparedPrompt | undefined
  sessionId: string | undefined
  taskId: string | undefined
  interactiveSandboxChat: boolean
  directTurnTelemetryContext?: DirectTurnTelemetryContext
  enabled: boolean
}): {
  pending: boolean
  error: string | undefined
} {
  const attemptedSessionRef = useRef<string | undefined>(undefined)
  const [outcome, setOutcome] = useState<{sessionId: string; error?: string}>()
  const currentOutcome = outcome?.sessionId === sessionId ? outcome : undefined
  const replay = useEffectEvent(async () => {
    if (!session || !prompt || !sessionId || !taskId) return

    let submissionId: number | undefined
    try {
      const receipt = await session.input.submit(
        prompt,
        undefined,
        async () => {
          if (interactiveSandboxChat) await session.commands.claimActiveClient(HOST_INDEPENDENT_CLIENT_TOOLS)
          submissionId = markUserStartedTurn(taskId)
        },
        directTurnTelemetryContext,
      )
      if (submissionId !== undefined) bindUserStartedTurn(taskId, submissionId, receipt)
      setOutcome({sessionId})
    } catch (error) {
      if (submissionId !== undefined) unmarkUserStartedTurn(taskId, submissionId)
      setOutcome({sessionId, error: error instanceof Error ? error.message : String(error)})
    }
  })

  useEffect(() => {
    if (!enabled || !session || !prompt || !sessionId || !taskId) return

    const evaluate = () => {
      if (attemptedSessionRef.current === sessionId) return
      const {state, passive, passiveKnown} = session.view.getSnapshot()
      if (!state || !passiveKnown) return
      if (passive) return
      attemptedSessionRef.current = sessionId
      if (!isUnstartedChat(state)) {
        setOutcome({sessionId})
        return
      }
      void replay()
    }

    const unsubscribe = session.view.subscribe(evaluate)
    evaluate()
    return unsubscribe
  }, [enabled, interactiveSandboxChat, prompt, session, sessionId, taskId])

  return {
    pending: enabled && currentOutcome === undefined,
    error: currentOutcome?.error ? `Failed to recover the first message: ${currentOutcome.error}` : undefined,
  }
}
